import {
  HHBox,
  HHButton,
  HHStack,
  HHTable,
  HHTableBody,
  HHTableCell,
  HHTableContainer,
  HHTableHead,
  HHTableRow,
  HHTooltip,
  HHTypography,
} from '@hinge-health/react-component-library';
import { useState } from 'react';
import { FILE_PARSING_BATCH_STATUS_LABELS } from '../constants/partner-data-gateway.constant';
import { BatchHeatmapCell } from '../domain/types';
import {
  formatByteRange,
  formatCount,
  formatRelativeTime,
} from '../utils/format.utils';
import { CopyToClipboard } from './copy-to-clipboard';
import { StatusChip, STATUS_CHIP_TEST_ID } from './status-chip';

export const BATCH_TABLE_TEST_ID = 'pdg-batch-table';
export const BATCH_TABLE_EMPTY_TEST_ID = 'pdg-batch-table-empty';
export const BATCH_TABLE_ROW_TEST_ID_PREFIX = 'pdg-batch-table-row';
export const BATCH_TABLE_ERROR_TOGGLE_TEST_ID_PREFIX =
  'pdg-batch-table-error-toggle';

export const batchTableRowTestId = (batchId: string): string =>
  `${BATCH_TABLE_ROW_TEST_ID_PREFIX}-${batchId}`;

export const batchTableErrorToggleTestId = (batchId: string): string =>
  `${BATCH_TABLE_ERROR_TOGGLE_TEST_ID_PREFIX}-${batchId}`;

/** Batch ids are UUIDs; the leading segment is enough to recognise a row. */
const TRUNCATED_ID_LENGTH = 8;

const truncateId = (batchId: string): string =>
  batchId.length > TRUNCATED_ID_LENGTH
    ? `${batchId.slice(0, TRUNCATED_ID_LENGTH)}…`
    : batchId;

const BatchIdCell = ({ batchId }: { batchId: string }): JSX.Element => (
  <HHStack direction="row" alignItems="center" gap={0.5}>
    <HHTooltip hhVariant="variant-bypass" title={batchId}>
      <HHBox>
        <HHTypography hhVariant="variant-bypass" variant="body2">
          {truncateId(batchId)}
        </HHTypography>
      </HHBox>
    </HHTooltip>
    <CopyToClipboard
      data={batchId}
      label={`Copy batch id ${batchId}`}
      testId={`copy-batch-id-${batchId}`}
    />
  </HHStack>
);

/**
 * Error messages from PDGS can be a full stack trace, which would blow the
 * row height out, so the cell shows one line until it is clicked.
 */
const ErrorCell = ({
  batchId,
  errorMessage,
}: {
  batchId: string;
  errorMessage: string;
}): JSX.Element => {
  const [expanded, setExpanded] = useState(false);

  return (
    <HHButton
      hhVariant="variant-bypass"
      variant="text"
      size="small"
      color="error"
      aria-expanded={expanded}
      data-testid={batchTableErrorToggleTestId(batchId)}
      onClick={(): void => setExpanded(current => !current)}
      sx={{
        textAlign: 'left',
        textTransform: 'none',
        padding: 0,
        maxWidth: 280,
        // Collapsed, the message is clamped to a single line; expanded, it
        // wraps to as many as it needs.
        ...(expanded
          ? { whiteSpace: 'normal', wordBreak: 'break-word' }
          : {
              display: 'block',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }),
      }}
    >
      {errorMessage}
    </HHButton>
  );
};

export interface BatchTableProps {
  batches: BatchHeatmapCell[];
}

/**
 * Row-per-batch detail behind the heatmap: the same data a cell's tooltip
 * shows, but copyable, sortable by eye, and with full error messages.
 */
export const BatchTable = ({ batches }: BatchTableProps): JSX.Element => {
  if (batches.length === 0) {
    return (
      <HHBox
        data-testid={BATCH_TABLE_EMPTY_TEST_ID}
        sx={{ textAlign: 'center', paddingY: 4 }}
      >
        <HHTypography hhVariant="variant-bypass" variant="body2">
          No batches to show.
        </HHTypography>
      </HHBox>
    );
  }

  return (
    <HHTableContainer data-testid={BATCH_TABLE_TEST_ID}>
      <HHTable aria-label="Parsing batches" size="small">
        <HHTableHead>
          <HHTableRow>
            <HHTableCell>Batch ID</HHTableCell>
            <HHTableCell>Byte Range</HHTableCell>
            <HHTableCell>Status</HHTableCell>
            <HHTableCell>Rows</HHTableCell>
            <HHTableCell>Error</HHTableCell>
            <HHTableCell>Started</HHTableCell>
          </HHTableRow>
        </HHTableHead>
        <HHTableBody>
          {batches.map(batch => (
            <HHTableRow
              key={batch.batchId}
              hover
              data-testid={batchTableRowTestId(batch.batchId)}
            >
              <HHTableCell>
                <BatchIdCell batchId={batch.batchId} />
              </HHTableCell>
              <HHTableCell>
                {formatByteRange(batch.byteOffsetStart, batch.byteOffsetEnd)}
              </HHTableCell>
              <HHTableCell>
                <StatusChip
                  status={batch.status}
                  label={FILE_PARSING_BATCH_STATUS_LABELS[batch.status]}
                  testId={`${STATUS_CHIP_TEST_ID}-${batch.batchId}`}
                />
              </HHTableCell>
              <HHTableCell>{formatCount(batch.rowCount)}</HHTableCell>
              <HHTableCell>
                {batch.errorMessage === null ? (
                  '—'
                ) : (
                  <ErrorCell
                    batchId={batch.batchId}
                    errorMessage={batch.errorMessage}
                  />
                )}
              </HHTableCell>
              {/* A batch that has not started yet has no timestamp — PDGS
                  only sets `startedAt` when a worker picks it up. */}
              <HHTableCell>
                {batch.startedAt === null
                  ? '—'
                  : formatRelativeTime(batch.startedAt)}
              </HHTableCell>
            </HHTableRow>
          ))}
        </HHTableBody>
      </HHTable>
    </HHTableContainer>
  );
};
